import type { GameRules, MatchOutcome, MatchSetup, Rng } from "@obean/game-contract";
import { createRng } from "./rng.js";
import { array, bytes, defineSnapshot, struct, u32 } from "./schema.js";
import { FixedStepper } from "./stepper.js";

/**
 * How a player's inputs wait for steps (P2-RT-04). Each step takes the oldest waiting input, so a
 * burst (what a stalled connection delivers at once) is applied in order instead of skipped.
 */
export interface InputQueue {
  /** Inputs waiting per player; beyond it the oldest are dropped. 1 means the newest wins. */
  size: number;
  /** A backlog standing this many steps in a row loses one waiting input, so latency comes down. */
  drainAfter: number;
}

/**
 * The default: the newest input wins. Right for a host that submits input as it's made (a local
 * game), where several can come between steps.
 */
export const LATEST_WINS: InputQueue = { size: 1, drainAfter: Number.POSITIVE_INFINITY };

/**
 * For a server whose clients each send one input per step (the protocol's one `match.in` per input
 * tick). Measured on the network harness (P2-RT-04, README): against latest-wins, hard snaps on the
 * poor profile fall from 13 to 4 a minute and skipped inputs on typical mobile from 19% to 6%, for
 * about 17 ms more median input delay.
 */
export const NETWORK_INPUT_QUEUE: InputQueue = { size: 4, drainAfter: 5 };

export interface RunnerOptions {
  /** Injected clock in ms. */
  now: () => number;
  /** Max steps per update() when the host timer is late (default 5). */
  maxCatchUp?: number;
  /** Default LATEST_WINS. A server whose clients send one input per step queues (P2-RT-04). */
  inputQueue?: InputQueue;
}

/** Runner snapshot layout version (wraps the game's own snapshot). */
export const RUNNER_SNAPSHOT_VERSION = 1;

/** The runner's envelope around the game's snapshot. Append-only, like any snapshot schema. */
const runnerSnapshot = defineSnapshot(
  struct({
    tick: u32(),
    lastSeq: array(u32(), { max: 8 }), // manifest players.max <= 8
    game: bytes({ max: 1 << 20 }),
  }),
  { version: RUNNER_SNAPSHOT_VERSION },
);

/**
 * Runs one match of any game through the contract: fixed steps from an injected clock, latest-wins
 * input per player with sequence numbers (stale and invalid inputs are dropped), per-player acks for
 * client prediction (P2-SIM-03), and snapshot/restore so a restarted host resumes the same match.
 *
 * Host-agnostic: the Room DO, a browser (prediction) or a local host device call `update()` from their
 * own timer. The runner never schedules anything itself.
 */
export class MatchRunner<S, I> {
  readonly rules: GameRules<S, I>;
  #state: S;
  #rng: Rng;
  #stepper: FixedStepper;
  #inputs: I[];
  /** Accepted inputs not yet stepped, oldest first, per slot. */
  #queues: { seq: number; input: I }[][];
  readonly #queue: InputQueue;
  /** Steps in a row each slot has had inputs left waiting. */
  #backlog: number[];
  #lastSeq: number[];
  #appliedSeq: number[];
  #outcome: MatchOutcome | null = null;
  #tick: number;

  private constructor(
    rules: GameRules<S, I>,
    state: S,
    rng: Rng,
    slots: number,
    tick: number,
    options: RunnerOptions,
    seqs?: number[],
  ) {
    this.rules = rules;
    this.#state = state;
    this.#rng = rng;
    this.#tick = tick;
    this.#inputs = Array.from({ length: slots }, () => rules.idleInput());
    this.#queues = Array.from({ length: slots }, () => []);
    this.#queue = options.inputQueue ?? LATEST_WINS;
    this.#backlog = Array.from({ length: slots }, () => 0);
    this.#lastSeq = seqs ? [...seqs] : Array.from({ length: slots }, () => 0);
    this.#appliedSeq = [...this.#lastSeq];
    this.#stepper = new FixedStepper(
      {
        stepMs: 1000 / rules.manifest.tickHz,
        now: options.now,
        ...(options.maxCatchUp === undefined ? {} : { maxCatchUp: options.maxCatchUp }),
      },
      tick,
    );
    this.#outcome = rules.outcome(state);
  }

  static start<S, I>(
    rules: GameRules<S, I>,
    setup: MatchSetup,
    options: RunnerOptions,
  ): MatchRunner<S, I> {
    const rng = createRng(setup.seed);
    const state = rules.init(setup, rng);
    return new MatchRunner(rules, state, rng, setup.players.length, 0, options);
  }

  /**
   * Accept a player's input if it's newer than the last one from that slot and valid for the game.
   * It waits in the slot's queue for a step, and applies to every step until the next one is taken
   * (held-state semantics).
   */
  submitInput(slot: number, seq: number, raw: unknown): boolean {
    const queue = this.#queues[slot];
    if (!Number.isInteger(slot) || !queue) return false;
    if (!Number.isInteger(seq) || seq <= (this.#lastSeq[slot] ?? 0)) return false;
    const input = this.rules.parseInput(raw);
    if (input === null) return false;
    queue.push({ seq, input });
    if (queue.length > Math.max(1, this.#queue.size)) queue.shift();
    this.#lastSeq[slot] = seq;
    return true;
  }

  /** Mark a slot as having no fresh input (disconnected or reserved seat): use the game's idle input. */
  idle(slot: number): void {
    if (slot < 0 || slot >= this.#inputs.length) return;
    this.#inputs[slot] = this.rules.idleInput();
    this.#queues[slot] = [];
  }

  /**
   * The slot's player came back with a fresh client, whose input seqs start again at 1 (a reload
   * or a reconnect, P2-RT-06): its numbering and waiting inputs are forgotten, and it idles until
   * the first new input.
   */
  rejoin(slot: number): void {
    if (slot < 0 || slot >= this.#inputs.length) return;
    this.idle(slot);
    this.#lastSeq[slot] = 0;
    this.#appliedSeq[slot] = 0;
    this.#backlog[slot] = 0;
  }

  /** Run all steps due by the clock (capped). Returns how many ran; 0 once the match has an outcome. */
  update(): number {
    if (this.#outcome) return 0;
    const due = this.#stepper.due();
    let ran = 0;
    for (; ran < due; ran++) {
      this.#queues.forEach((queue, slot) => {
        this.#take(queue, slot);
      });
      this.#state = this.rules.step(this.#state, this.#inputs, this.#rng);
      this.#tick++;
      this.#outcome = this.rules.outcome(this.#state);
      if (this.#outcome) {
        ran++;
        break;
      }
    }
    return ran;
  }

  /** This step's input for a slot: the oldest waiting one, if any; otherwise the last one holds. */
  #take(queue: { seq: number; input: I }[], slot: number): void {
    let next = queue.shift();
    if (!next) return;
    const backlog = queue.length > 0 ? (this.#backlog[slot] ?? 0) + 1 : 0;
    if (backlog >= this.#queue.drainAfter) {
      next = queue.shift() ?? next; // skip one: the backlog has stood long enough
      this.#backlog[slot] = 0;
    } else this.#backlog[slot] = backlog;
    this.#inputs[slot] = next.input;
    this.#appliedSeq[slot] = next.seq;
  }

  get state(): S {
    return this.#state;
  }

  get tick(): number {
    return this.#tick;
  }

  get outcome(): MatchOutcome | null {
    return this.#outcome;
  }

  /** Last input seq from `slot` that a simulated step has used (for client reconciliation). */
  ack(slot: number): number {
    return this.#appliedSeq[slot] ?? 0;
  }

  /** Steps dropped because the host fell too far behind (telemetry). */
  get droppedSteps(): number {
    return this.#stepper.dropped;
  }

  /**
   * Runner snapshot (tick, input seqs, the game's own snapshot): enough to resume the match after a
   * host restart (ADR 0004 D1). Readable by the previous and the next runner version.
   */
  snapshot(): Uint8Array {
    return runnerSnapshot.encode({
      tick: this.#tick,
      lastSeq: this.#lastSeq,
      game: this.gameSnapshot(),
    });
  }

  /** The game's own snapshot of the current state (what clients receive in snapshot frames). */
  gameSnapshot(): Uint8Array {
    return this.rules.encodeSnapshot(this.#state, this.#rng);
  }

  static restore<S, I>(
    rules: GameRules<S, I>,
    data: Uint8Array,
    options: RunnerOptions,
  ): MatchRunner<S, I> {
    const { tick, lastSeq, game } = runnerSnapshot.decode(data).value;
    const rng = createRng(0);
    const state = rules.decodeSnapshot(game, rng);
    return new MatchRunner(rules, state, rng, lastSeq.length, tick, options, lastSeq);
  }
}
